// Real Google Fonts families that genuinely support each script — loaded
// live via the Google Fonts CSS API so the picker can preview them
// accurately, not just label them. Malayalam's Google Fonts catalog is
// smaller than the others, so it's padded with distinct weights of the
// same families (still real, distinct, selectable styles) to reach 10.
export type CaptionLanguage = "english" | "hindi" | "tamil" | "telugu" | "malayalam";

export const CAPTION_LANGUAGES: { id: CaptionLanguage; label: string }[] = [
  { id: "english", label: "English" },
  { id: "hindi", label: "Hindi" },
  { id: "tamil", label: "Tamil" },
  { id: "telugu", label: "Telugu" },
  { id: "malayalam", label: "Malayalam" },
];

export const CAPTION_FONTS: Record<CaptionLanguage, string[]> = {
  english: ["Inter", "Roboto", "Open Sans", "Montserrat", "Poppins", "Lato", "Nunito", "Oswald", "Bebas Neue", "Anton"],
  hindi: ["Noto Sans Devanagari", "Noto Serif Devanagari", "Hind", "Mukta", "Tiro Devanagari Hindi", "Baloo 2", "Khand", "Teko", "Rajdhani", "Yatra One"],
  tamil: ["Noto Sans Tamil", "Noto Serif Tamil", "Hind Madurai", "Catamaran", "Mukta Malar", "Baloo Thambi 2", "Pavanam", "Kavivanar", "Meera Inimai", "Anek Tamil"],
  telugu: ["Noto Sans Telugu", "Noto Serif Telugu", "Mallanna", "Ramabhadra", "Tenali Ramakrishna", "Gurajada", "Lakki Reddy", "Mandali", "NTR", "Suranna"],
  malayalam: [
    "Noto Sans Malayalam",
    "Noto Serif Malayalam",
    "Manjari",
    "Baloo Chettan 2",
    "Gayathri",
    "Chilanka",
    "Anek Malayalam",
    "Noto Sans Malayalam Medium",
    "Noto Sans Malayalam SemiBold",
    "Noto Serif Malayalam Bold",
  ],
};

// Some "fonts" above are really a base family + weight (for languages with
// a smaller real font catalog) — strip that back to the actual Google
// Fonts family name for loading/embedding purposes.
export function baseFontFamily(font: string): string {
  return font.replace(/\s+(Medium|SemiBold|Bold)$/, "");
}

export function googleFontsStylesheetUrl(fonts: string[]): string {
  const families = Array.from(new Set(fonts.map(baseFontFamily)))
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700`)
    .join("&");
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

// ASS/SRT/VTT only ever reference a font by NAME — none of them embed the
// actual font file. The web preview renders correctly because the browser
// loaded it from Google Fonts; a video editor reads the font name from the
// file and silently substitutes a fallback (usually with no Tamil/Hindi/
// Telugu/Malayalam glyphs at all) unless the real font is installed on
// that machine's OS first. This links to where to get it.
export function googleFontsSpecimenUrl(font: string): string {
  const slug = baseFontFamily(font).replace(/\s+/g, "+");
  return `https://fonts.google.com/specimen/${slug}`;
}
